import classNames from "classnames"

import { Color } from "../../../constants/V2/color"
import { getTextColorClass } from "../../../utils/V2/color"

import { createElement } from "react"

export type ResponsiveTypographySize =
  | "display1"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "lead-lg"
  | "lead-md"
  | "lead-sm"
  | "body-lg"
  | "body-md"
  | "body-sm"
  | "subscript-lg"
  | "subscript-md"
  | "subscript-sm"

export type FixedTypographySize =
  | "display1-fixed"
  | "h1-fixed"
  | "h2-fixed"
  | "h3-fixed"
  | "h4-fixed"
  | "h5-fixed"
  | "lead-lg-fixed"
  | "lead-md-fixed"
  | "lead-sm-fixed"
  | "body-lg-fixed"
  | "body-md-fixed"
  | "body-sm-fixed"
  | "subscript-lg-fixed"
  | "subscript-md-fixed"
  | "subscript-sm-fixed"

export type DualTypographySize =
  | "lead-lg-dual"
  | "lead-sm-dual"
  | "body-md-dual"
  | "body-lg-dual"

export type HeroTypographySize = "text-hero-lead-lg" | "text-hero-h3"

export type TypographySize =
  | DualTypographySize
  | ResponsiveTypographySize
  | FixedTypographySize
  | HeroTypographySize

type Element = "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "span" | "label"

type Props = {
  id?: string
  text: string
  size: TypographySize
  font: "grotesk" | "grotesk-25" | "grotesk-10"
  weight: "book" | "medium"
  color: Color
  element?: Element
  className?: string
  onClick?: () => any
  leadingNone?: boolean
  "data-testid"?: string
  highlightedText?: string | string[]
  highlightColor?: Color
  highlightSize?: TypographySize
  highlightWeight?: "book" | "medium"
  highlightClassName?: string
}

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)

const Typography = ({
  id,
  text,
  size,
  font,
  weight,
  color,
  element = "p",
  className,
  onClick,
  leadingNone = false,
  "data-testid": dataTestId,
  highlightedText,
  highlightColor = Color.Blue,
  highlightSize = size,
  highlightWeight = weight,
  highlightClassName,
}: Props) => {
  /*
    Note: Since this component is the only way in which text should be added to the site,
    both font size and family can be resolved locally here instead of being exported globally.
  */
  const getFontSizeClass = (fontSize: TypographySize) => {
    switch (fontSize) {
      case "display1":
        return "text-responsive-display1"
      case "display1-fixed":
        return "text-display1"
      case "h1":
        return "text-responsive-h1"
      case "h1-fixed":
        return "text-h1"
      case "h2":
        return "text-responsive-h2"
      case "h2-fixed":
        return "text-h2"
      case "h3":
        return "text-responsive-h3"
      case "h3-fixed":
        return "text-h3"
      case "h4":
        return "text-responsive-h4"
      case "h4-fixed":
        return "text-h4"
      case "h5":
        return "text-responsive-h5"
      case "h5-fixed":
        return "text-h5"
      case "lead-lg":
        return "text-responsive-lead-lg"
      case "lead-lg-fixed":
        return "text-lead-lg"
      case "lead-md":
        return "text-responsive-lead-md"
      case "lead-md-fixed":
        return "text-lead-md"
      case "lead-sm":
        return "text-responsive-lead-sm"
      case "lead-sm-fixed":
        return "text-lead-sm"
      case "body-lg":
        return "text-responsive-body-lg"
      case "body-lg-fixed":
        return "text-body-lg"
      case "body-md":
        return "text-responsive-body-md"
      case "body-md-fixed":
        return "text-body-md"
      case "body-sm":
        return "text-responsive-body-sm"
      case "body-sm-fixed":
        return "text-body-sm"
      case "subscript-lg":
        return "text-responsive-subscript-lg"
      case "subscript-lg-fixed":
        return "text-subscript-lg"
      case "subscript-md":
        return "text-responsive-subscript-md"
      case "subscript-md-fixed":
        return "text-subscript-md"
      case "subscript-sm":
        return "text-responsive-subscript-sm"
      case "subscript-sm-fixed":
        return "text-subscript-sm"
      case "lead-lg-dual":
        return "text-dual-lead-lg"
      case "lead-sm-dual":
        return "text-dual-lead-sm"
      case "body-md-dual":
        return "text-dual-body-md"
      case "body-lg-dual":
        return "text-dual-body-lg"
      case "text-hero-lead-lg":
        return "text-hero-lead-lg"
      case "text-hero-h3":
        return "text-hero-h3"
      default:
        return ""
    }
  }

  const getFontFamilyClass = (fontFamily: string) => {
    switch (fontFamily) {
      case "grotesk":
        return "font-grotesk"
      case "grotesk-25":
        return "font-grotesk-25"
      case "grotesk-10":
        return "font-grotesk-10"
      default:
        return ""
    }
  }

  const highlightClasses = classNames(
    getTextColorClass(highlightColor),
    getFontSizeClass(highlightSize),
    highlightWeight === "book" ? "font-normal" : "font-medium",
    highlightClassName
  )

  const getHighlightedChildren = () => {
    if (typeof highlightedText === "string") {
      if (!highlightedText || !text.includes(highlightedText)) return text
      return [
        text.split(highlightedText)[0],
        createElement(
          "span",
          { key: highlightedText, className: highlightClasses },
          highlightedText
        ),
        text.split(highlightedText)[1],
      ]
    }

    const wordsToHighlight = (highlightedText ?? [])
      .filter((word) => word && text.includes(word))
      .sort((firstWord, secondWord) => secondWord.length - firstWord.length)

    if (!wordsToHighlight.length) return text

    return text
      .split(new RegExp(`(${wordsToHighlight.map(escapeRegExp).join("|")})`))
      .map((part, index) =>
        wordsToHighlight.includes(part)
          ? createElement(
              "span",
              { key: `${part}-${index}`, className: highlightClasses },
              part
            )
          : part
      )
  }

  const children = getHighlightedChildren()

  return createElement(
    element,
    {
      id: id,
      className: classNames(
        getFontSizeClass(size),
        getFontFamilyClass(font),
        getTextColorClass(color),
        weight === "book" ? "font-normal" : "font-medium",
        { "!leading-none": leadingNone },
        className
      ),
      onClick,
      "data-testid": dataTestId,
    },
    children
  )
}

export default Typography
